import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import React, { useEffect, useMemo, useState } from 'react';
import Lozenge from '@atlaskit/lozenge';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	JoinableProductSpotlight,
	type SupportedProducts,
} from '@atlassian/cross-joinable-products-in-switcher';

import { ItemWithAvatarGroup } from '../../primitives';
import { fetchJson } from '../../../common/utils/fetch';
import { type JoinableSiteItemType } from '../../../cross-join/utils/cross-join-links';
import { SWITCHER_SOURCE, ViewedTracker } from '../../../common/utils/analytics';
import { type MessageDescriptor, useIntl } from 'react-intl-next';
import { type SwitcherProductType, type GenericShowFlag } from '../../../types';

import messages from './messages';
import { token } from '@atlaskit/tokens';
import SuccessIcon from '@atlaskit/icon/core/migration/check-mark--check';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { Inline } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	NAVIGATION_CHANNEL,
	UI_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
} from '../../../common/utils/analytics';
import { SCREEN_EVENT_TYPE, TRACK_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { AppIcon } from '../../primitives/app-icon';

// https://stash.atlassian.com/projects/IPS/repos/id-invitations-service/browse/src/main/java/com/atlassian/identity/invitations/model/access/AccessRequestCapability.java
export enum AccessRequestCapabilityType {
	DIRECT_ACCESS = 'DIRECT_ACCESS',
	REQUEST_ACCESS = 'REQUEST_ACCESS',
	ACCESS_EXISTS = 'ACCESS_EXISTS',
	ACCESS_GRANTED = 'ACCESS_GRANTED',
	PENDING_REQUEST_EXISTS = 'PENDING_REQUEST_EXISTS',
	PENDING_REQUEST_CREATED = 'PENDING_REQUEST_CREATED',
	DENIED_REQUEST_EXISTS = 'DENIED_REQUEST_EXISTS',
	APPROVED_REQUEST_EXISTS = 'APPROVED_REQUEST_EXISTS',
	ERROR = 'ERROR',
}

type InlineJoinResponse = {
	results: {
		[key: string]: AccessRequestCapabilityType;
	};
};

type ActionType = 'request' | 'join';

const productMap: {
	[key in SwitcherProductType]?: {
		id: string;
		description: MessageDescriptor;
		productName: string;
		requestAccessStatus: MessageDescriptor;
		directAccessStatus: MessageDescriptor;
	};
} = {
	JIRA_SOFTWARE: {
		id: 'jira-software',
		productName: 'Jira',
		description: messages.jiraDescriptionText,
		requestAccessStatus: messages.joinTeamStatusText,
		directAccessStatus: messages.instantAccessStatusText,
	},
	CONFLUENCE: {
		id: 'confluence',
		productName: 'Confluence',
		description: messages.confluenceDescriptionText,
		requestAccessStatus: messages.joinTeamStatusText,
		directAccessStatus: messages.instantAccessStatusText,
	},
	JIRA_PRODUCT_DISCOVERY: {
		id: 'jira-product-discovery',
		productName: 'Jira Product Discovery',
		description: messages.jpdDescriptionText,
		requestAccessStatus: messages.joinStatusText,
		directAccessStatus: messages.joinStatusText,
	},
	JIRA_SERVICE_DESK: {
		id: 'jira-servicedesk',
		productName: 'Jira Service Management',
		description: messages.jsmDescriptionText,
		requestAccessStatus: messages.joinStatusText,
		directAccessStatus: messages.joinStatusText,
	},
};

const SUPPORT_LINK = 'https://support.atlassian.com/contact/';
const JOIN_ACTION = 'join';
const REQUEST_ACTION = 'request';

function getPrioritisedProduct(joinableSiteLinks: JoinableSiteItemType[]) {
	const sortedProducts = joinableSiteLinks.sort((a, b) => {
		if (a.productType === 'JIRA_SOFTWARE' || a.productType === 'CONFLUENCE') {
			return -1;
		}

		if (b.productType === 'JIRA_SOFTWARE' || b.productType === 'CONFLUENCE') {
			return 1;
		}
		return -1;
	});
	const directAccess = sortedProducts.filter((link) => link.capability === 'DIRECT_ACCESS');

	return directAccess[0] || sortedProducts[0];
}

const REQUEST_ACCESS_URL = '/gateway/api/invitations/v1/access-requests/request';
export async function requestAccessCall(
	product: string,
	cloudId: string,
): Promise<
	| { status: true; results: InlineJoinResponse['results'] }
	| { status: false; results?: InlineJoinResponse['results'] }
> {
	try {
		const response = await fetchJson<InlineJoinResponse>(REQUEST_ACCESS_URL, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				resource: `ari:cloud:${product}::site/${cloudId}`,
			}),
		});
		const result = response.results[`ari:cloud:${product}::site/${cloudId}`];
		if (
			result === AccessRequestCapabilityType.ACCESS_GRANTED ||
			result === AccessRequestCapabilityType.ACCESS_EXISTS ||
			result === AccessRequestCapabilityType.PENDING_REQUEST_CREATED
		) {
			return { status: true, results: response.results };
		}
		return { status: false, results: response?.results };
	} catch (e) {
		return { status: false };
	}
}

export const JoinProductItem = ({
	joinableSiteLinks,
	showFlag,
}: {
	joinableSiteLinks: JoinableSiteItemType[];
	showFlag?: GenericShowFlag<any>;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [requestSent, setRequestSent] = useState(false);
	const [showSpotlight, setShowSpotlight] = useState(false);

	const fireProductAccessRequested = async (
		product: string,
		result: InlineJoinResponse['results'],
	) => {
		const analyticsAttributes = {
			products: [product],
			inviteResult: [result],
			integration: SWITCHER_SOURCE,
			selfRequest: true,
		};
		createAnalyticsEvent({
			eventType: TRACK_EVENT_TYPE,
			action: 'requested',
			actionSubject: 'productAccess',
			attributes: {
				...analyticsAttributes,
			},
		}).fire(NAVIGATION_CHANNEL);
	};

	const fireActionedButtonClickEvent = (product: string, actionType: ActionType) => {
		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'actionedJoinProductSpotlight',
			attributes: {
				product,
				actionType,
			},
		}).fire(NAVIGATION_CHANNEL);
	};

	const fireJoinProductItemClicked = (product: string, actionType: ActionType) => {
		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'joinProductItem',
			attributes: {
				product,
				actionType,
			},
		}).fire(NAVIGATION_CHANNEL);
	};

	const onDismissItemClicked = () => {
		setShowSpotlight(false);
		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'dismissedJoinProductSpotlight',
		}).fire(NAVIGATION_CHANNEL);
	};

	const onRequestAccessError = () => {
		createAnalyticsEvent({
			eventType: OPERATIONAL_EVENT_TYPE,
			action: 'failed',
			actionSubject: 'switcherRequestAccess',
			actionSubjectId: 'joinProductSpotlight',
		}).fire(NAVIGATION_CHANNEL);
	};

	const onGetStartedItemRequestAccessClicked = (
		product: string,
		cloudId: string,
		productName: string,
	) => {
		fireActionedButtonClickEvent(product, 'request');
		handleRaFlow(product, cloudId, productName);
		setShowSpotlight(false);
	};

	const onGetStartedItemJoinClicked = (product: string, href: string | undefined) => {
		fireActionedButtonClickEvent(product, 'join');
		setShowSpotlight(false);
		href && window.open(href, '_blank');
	};

	useEffect(() => {
		// Wait for display animations prior to rendering spotlight
		setTimeout(() => setShowSpotlight(true), 500);
	}, []);

	const handleRaFlow = async (product: string, cloudId: string, productName: string) => {
		const raResponse = await requestAccessCall(product, cloudId);
		if (raResponse?.status) {
			setRequestSent(true);
			showFlag?.({
				appearance: 'success',
				title: formatMessage(messages.requestAccessPendingRequestFlagTitle),
				description: formatMessage(messages.requestAccessPendingRequestFlagDescription),
				icon: <SuccessIcon spacing="spacious" label="" color={token('color.icon.success')} />,
				isAutoDismiss: true,
			});
			fireProductAccessRequested(product, raResponse.results);
			return;
		}
		onRequestAccessError();
		showFlag?.({
			appearance: 'error',
			title: formatMessage(messages.requestAccessPendingRequestFlagErrorTitle, {
				product: productName,
			}),
			description: formatMessage(messages.requestAccessPendingRequestFlagErrorDescription, {
				a: (chunks: (string | React.ReactNode)[]) =>
					fg('dst-a11y__replace-anchor-with-link__navigation-exp') ? (
						<Link href={SUPPORT_LINK}>{chunks}</Link>
					) : (
						// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
						<a href={SUPPORT_LINK}>{chunks}</a>
					),
			}),
			icon: (
				<ErrorIcon
					label="error"
					spacing="spacious"
					color={token('color.icon.danger')}
					LEGACY_secondaryColor={token('color.icon.inverse')}
				/>
			),
			isAutoDismiss: true,
		});
	};
	const cohort = FeatureGates.getExperimentValue<'variation' | 'not-enrolled' | 'control'>(
		'joinable_products_in_switcher',
		'cohort',
		'not-enrolled',
		{
			fireExperimentExposure: false,
		},
	);

	const { cloudId, users, href, iconKey, label, productType, capability } =
		getPrioritisedProduct(joinableSiteLinks) || {};

	const eligible = useMemo(
		() =>
			joinableSiteLinks.length > 0 &&
			(cohort === 'variation' || cohort === 'control') &&
			!!productType,
		[productType, joinableSiteLinks.length, cohort],
	);

	const isEnabled = eligible && cohort === 'variation';

	useEffect(() => {
		if (eligible) {
			FeatureGates.manuallyLogExperimentExposure('joinable_products_in_switcher');
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'exposed',
				actionSubject: 'feature',
				attributes: {
					flagKey: 'joinable_products_in_switcher',
					cohort,
					value: cohort,
				},
			}).fire(NAVIGATION_CHANNEL);
			createAnalyticsEvent({
				eventType: SCREEN_EVENT_TYPE,
				name: 'joinProductScreen',
				attributes: {
					eligibleProducts: joinableSiteLinks.length,
					joinProductExperiment: cohort,
				},
			}).fire(NAVIGATION_CHANNEL);
		}
	}, [eligible, cohort, createAnalyticsEvent, joinableSiteLinks.length, cloudId, productType]);

	if (!isEnabled || !productType) {
		return null;
	}

	const mappedProductType = productMap[productType];

	if (
		mappedProductType?.description &&
		(capability === 'DIRECT_ACCESS' || capability === undefined)
	) {
		return (
			<li>
				<JoinableProductSpotlight
					product={productType as SupportedProducts}
					cloudId={cloudId}
					showSpotlight={showSpotlight}
					onDismissItemClicked={onDismissItemClicked}
					onGetStartedItemClicked={() => onGetStartedItemJoinClicked(mappedProductType.id, href)}
					analyticsChannel={NAVIGATION_CHANNEL}
				>
					<ViewedTracker
						subject="joinProductItem"
						data={{
							product: mappedProductType.id,
							eligibleProducts: joinableSiteLinks.length,
							joinProductExperiment: cohort,
							actionType: JOIN_ACTION,
						}}
					/>
					<ItemWithAvatarGroup
						icon={<AppIcon iconKey={iconKey} />}
						href={href}
						users={users}
						onItemClick={() => fireJoinProductItemClicked(mappedProductType.id, JOIN_ACTION)}
						target="_blank"
						rel="noreferrer"
						description={formatMessage(mappedProductType.description)}
						tag="div"
						maxCount={2}
					>
						<Inline space="space.100" alignBlock="center">
							{label}{' '}
							<Lozenge appearance="new">
								{formatMessage(mappedProductType.directAccessStatus)}{' '}
							</Lozenge>
						</Inline>
					</ItemWithAvatarGroup>
				</JoinableProductSpotlight>
			</li>
		);
	}

	if (mappedProductType?.id && capability === 'REQUEST_ACCESS') {
		return (
			<li>
				<JoinableProductSpotlight
					cloudId={cloudId}
					product={productType as SupportedProducts}
					showSpotlight={showSpotlight}
					onDismissItemClicked={onDismissItemClicked}
					onGetStartedItemClicked={() =>
						onGetStartedItemRequestAccessClicked(
							mappedProductType.id,
							cloudId,
							mappedProductType.productName,
						)
					}
					analyticsChannel={NAVIGATION_CHANNEL}
				>
					<ViewedTracker
						subject="joinProductItem"
						data={{
							product: mappedProductType.id,
							eligibleProducts: joinableSiteLinks.length,
							joinProductExperiment: cohort,
							actionType: REQUEST_ACTION,
						}}
					/>
					<ItemWithAvatarGroup
						icon={<AppIcon iconKey={iconKey} />}
						users={users}
						onItemClick={() => {
							fireJoinProductItemClicked(mappedProductType.id, REQUEST_ACTION);
							handleRaFlow(mappedProductType.id, cloudId, mappedProductType.productName);
						}}
						description={formatMessage(mappedProductType.description)}
						tag="div"
						maxCount={2}
					>
						<Inline space="space.100" alignBlock="center">
							{label}
							<Lozenge appearance="new">
								{requestSent
									? formatMessage(messages.requestSentStatusText)
									: formatMessage(mappedProductType.requestAccessStatus)}
							</Lozenge>
						</Inline>
					</ItemWithAvatarGroup>
				</JoinableProductSpotlight>
			</li>
		);
	}

	return null;
};
